import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaCalendar } from 'react-icons/fa';
import '../../styles/DateRangePicker.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const PRESETS = [
  { label: 'Today', key: 'today' },
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Last 7 Days', key: 'last7' },
  { label: 'Last 30 Days', key: 'last30' },
  { label: 'This Month', key: 'thisMonth' },
  { label: 'Last Month', key: 'lastMonth' },
  { label: 'This Year', key: 'thisYear' },
];

const getPresetRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: y, to: y };
    }
    case 'last7': {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      return { from: d, to: today };
    }
    case 'last30': {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      return { from: d, to: today };
    }
    case 'thisMonth':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: today };
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: start, to: end };
    }
    case 'thisYear':
      return { from: new Date(now.getFullYear(), 0, 1), to: today };
    default:
      return { from: null, to: null };
  }
};

const formatShort = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${String(d.getFullYear()).slice(2)}`;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isInRange = (day, from, to) => {
  if (!from || !to) return false;
  const d = day.getTime();
  return d >= from.getTime() && d <= to.getTime();
};

const DateRangePicker = ({ from, to, onChange }) => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [selFrom, setSelFrom] = useState(from ? new Date(from) : null);
  const [selTo, setSelTo] = useState(to ? new Date(to) : null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [activePreset, setActivePreset] = useState(from && to && from === to ? 'today' : null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (from) setSelFrom(new Date(from));
    if (to) setSelTo(new Date(to));
  }, [from, to]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Monday = 0
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDayClick = (day) => {
    setActivePreset(null);
    if (!selFrom || (selFrom && selTo)) {
      // Start new selection
      setSelFrom(day);
      setSelTo(null);
    } else {
      // Complete selection
      if (day < selFrom) {
        setSelTo(selFrom);
        setSelFrom(day);
        applyRange(day, selFrom);
      } else {
        setSelTo(day);
        applyRange(selFrom, day);
      }
    }
  };

  const applyRange = (fromDate, toDate) => {
    const fromStr = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`;
    const toStr = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`;
    onChange(fromStr, toStr);
    setTimeout(() => setOpen(false), 200);
  };

  const handlePreset = (key) => {
    setActivePreset(key);
    const { from: f, to: t } = getPresetRange(key);
    setSelFrom(f);
    setSelTo(t);
    setViewMonth(t.getMonth());
    setViewYear(t.getFullYear());
    applyRange(f, t);
  };

  const handleReset = () => {
    setSelFrom(null);
    setSelTo(null);
    setActivePreset(null);
    onChange('', '');
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const cells = [];

    // Previous month filler days
    const prevMonthDays = getDaysInMonth(viewMonth === 0 ? 11 : viewMonth - 1, viewMonth === 0 ? viewYear - 1 : viewYear);
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const m = viewMonth === 0 ? 11 : viewMonth - 1;
      const y = viewMonth === 0 ? viewYear - 1 : viewYear;
      const date = new Date(y, m, day);
      cells.push(
        <div key={`prev-${i}`} className="drp-day drp-day-other" onClick={() => { prevMonth(); handleDayClick(date); }}>
          {day}
        </div>
      );
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewYear, viewMonth, d);
      const isStart = isSameDay(date, selFrom);
      const isEnd = isSameDay(date, selTo);
      const isToday = isSameDay(date, new Date());

      // Determine hover range
      let inRange = false;
      if (selFrom && selTo) {
        inRange = isInRange(date, selFrom, selTo);
      } else if (selFrom && !selTo && hoveredDate) {
        const hStart = hoveredDate < selFrom ? hoveredDate : selFrom;
        const hEnd = hoveredDate < selFrom ? selFrom : hoveredDate;
        inRange = isInRange(date, hStart, hEnd);
      }

      const classes = [
        'drp-day',
        isStart ? 'drp-day-start' : '',
        isEnd ? 'drp-day-end' : '',
        inRange && !isStart && !isEnd ? 'drp-day-in-range' : '',
        isToday && !isStart && !isEnd ? 'drp-day-today' : '',
      ].filter(Boolean).join(' ');

      cells.push(
        <div
          key={d}
          className={classes}
          onClick={() => handleDayClick(date)}
          onMouseEnter={() => setHoveredDate(date)}
          onMouseLeave={() => setHoveredDate(null)}
        >
          {d}
        </div>
      );
    }

    // Next month filler days
    const totalCells = cells.length;
    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 1; i <= remaining; i++) {
      const m = viewMonth === 11 ? 0 : viewMonth + 1;
      const y = viewMonth === 11 ? viewYear + 1 : viewYear;
      const date = new Date(y, m, i);
      cells.push(
        <div key={`next-${i}`} className="drp-day drp-day-other" onClick={() => { nextMonth(); handleDayClick(date); }}>
          {i}
        </div>
      );
    }

    return cells;
  };

  const displayLabel = selFrom && selTo
    ? `${formatShort(selFrom)} – ${formatShort(selTo)}`
    : selFrom
      ? `${formatShort(selFrom)} – ...`
      : 'Select date range';

  return (
    <div className="drp-container" ref={containerRef}>
      <button className="drp-trigger" onClick={() => setOpen(!open)}>
        <FaCalendar className="drp-trigger-icon" />
        <span>{displayLabel}</span>
        <span className="drp-trigger-arrow">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="drp-popup">
          <div className="drp-presets">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                className={`drp-preset-btn ${activePreset === p.key ? 'drp-preset-active' : ''}`}
                onClick={() => handlePreset(p.key)}
              >
                {p.label}
              </button>
            ))}
            <button className="drp-preset-btn drp-reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>

          <div className="drp-calendar">
            <div className="drp-calendar-header">
              <span className="drp-month-label">{MONTHS[viewMonth]} {viewYear}</span>
              <div className="drp-nav">
                <button className="drp-nav-btn" onClick={prevMonth}><FaChevronLeft /></button>
                <button className="drp-nav-btn" onClick={nextMonth}><FaChevronRight /></button>
              </div>
            </div>

            <div className="drp-weekdays">
              {DAYS.map((d) => (
                <div key={d} className="drp-weekday">{d}</div>
              ))}
            </div>

            <div className="drp-days-grid">
              {renderCalendarDays()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
